/**
 * @copyright 汽车商贸管理平台 V1.0
 * @year 2026
 * @file dingtalk.js
 * @description 钉钉 H5 微应用免登工具（jsapi 加载 / dd.ready / 免登授权码）
 *
 * 免登链路：
 *   1. 在钉钉容器内打开（userAgent 含 DingTalk）
 *   2. 加载钉钉 jsapi（容器一般已注入 window.dd，加载脚本兜底）
 *   3. dd.ready 就绪后调 dd.runtime.permission.requestAuthCode({ corpId }) 拿临时 code
 *   4. code 交给后端 /api/User/DingTalkLogin 换系统登录 token
 */

// 钉钉官方 jsapi 候选地址（H5 微应用内使用；多版本兜底，避免某个版本 CDN 404）
const DINGTALK_JSAPI_URLS = [
  "https://g.alicdn.com/dingtalk/dingtalk-jsapi/2.10.11/dingtalk.open.js",
  "https://g.alicdn.com/dingtalk/dingtalk-jsapi/2.10.10/dingtalk.open.js",
  "https://g.alicdn.com/dingtalk/dingtalk-jsapi/2.10.9/dingtalk.open.js",
];

let jsPromise = null; // 避免重复注入 script

/**
 * 是否运行在钉钉容器内
 */
export function isDingTalkEnv() {
  return /DingTalk/i.test(navigator.userAgent || "");
}

/**
 * 等待 window.dd 出现（钉钉微应用客户端通常自动注入，可能晚于页面脚本）
 */
function waitForDd(timeoutMs) {
  return new Promise((resolve) => {
    const start = Date.now();
    const timer = setInterval(() => {
      if (window.dd) {
        clearInterval(timer);
        resolve(true);
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(timer);
        resolve(false);
      }
    }, 100);
  });
}

/**
 * 按候选地址逐个注入 jsapi 脚本
 */
function injectScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("script load fail: " + src));
    document.head.appendChild(s);
  });
}

/**
 * 加载钉钉 jsapi，resolve 后 window.dd 可用。
 * 顺序：容器自带 dd(等待3s) → 候选脚本逐个注入(每个再等1.5s) → 仍无则报错(带已尝试地址)。
 */
export async function loadDingTalkJs() {
  if (typeof window === "undefined") {
    throw new Error("非浏览器环境");
  }
  if (window.dd) return window.dd;
  if (jsPromise) return jsPromise;

  jsPromise = (async () => {
    // 1. 容器注入时机晚：先等 3 秒
    if (await waitForDd(3000)) return window.dd;

    // 2. 逐个候选脚本注入（等脚本生效后再探测一次）
    const tried = [];
    for (const url of DINGTALK_JSAPI_URLS) {
      tried.push(url);
      try {
        await injectScript(url);
      } catch (e) {
        continue; // 该地址加载失败，试下一个
      }
      if (await waitForDd(1500)) return window.dd;
    }
    jsPromise = null;
    throw new Error("钉钉 JSAPI 加载失败(已尝试:" + tried.join(" | ") + ")");
  })();

  return jsPromise;
}

/**
 * dd.ready（容器初始化完成）
 */
export function ddReady() {
  return new Promise((resolve, reject) => {
    if (!window.dd) {
      reject(new Error("钉钉容器未就绪"));
      return;
    }
    window.dd.ready(() => resolve());
    window.dd.error((err) =>
      reject(new Error((err && err.errorMessage) || "钉钉容器初始化失败"))
    );
  });
}

/**
 * 获取免登授权码
 * @param {string} corpId 企业 CorpId（由后端 /api/User/DingTalkCorp 返回）
 * @returns {Promise<string>} 一次性 authCode
 */
export function requestAuthCode(corpId) {
  return new Promise((resolve, reject) => {
    if (!window.dd || !window.dd.runtime || !window.dd.runtime.permission) {
      reject(new Error("钉钉容器未提供免登能力"));
      return;
    }
    window.dd.runtime.permission.requestAuthCode({
      corpId: corpId,
      onSuccess: (info) => {
        if (info && info.code) resolve(info.code);
        else reject(new Error("未获取到免登授权码"));
      },
      onFail: (err) =>
        reject(new Error((err && err.errorMessage) || "获取免登授权码失败")),
    });
  });
}

/**
 * 一键免登：加载 jsapi → dd.ready → 取 corpId → 取 authCode
 * @param {string} corpId
 * @returns {Promise<string>} authCode
 */
export async function getDingTalkAuthCode(corpId) {
  await loadDingTalkJs();
  await ddReady();
  return await requestAuthCode(corpId);
}

export default {};
